import { CustomToast } from "../models/ToastMessage";
import caseSummaryEndpoints from './endpoints/caseSummary.endpoints';

/**
 * Generates an AI-powered overview of the bail agent's dashboard.
 * Uses OpenAI to provide summary, priority items, and key observations.
 *
 * @param {Object} dashboardData - The dashboard data including metrics and defendants
 * @returns {Promise<Object|null>} AI overview response or null on error
 */
export async function getDashboardOverview(dashboardData) {
    try {
        const response = await caseSummaryEndpoints.getDashboardOverview(dashboardData);
        if (response.status === 200) {
            return response.data;
        }
        return null;
    } catch (error) {
        console.error('Error generating dashboard AI overview:', error);

        const errorMessage = error.response?.data?.errorMessage ||
                           error.response?.data?.message ||
                           'Failed to generate AI overview.';
        // Don't show toast for this - it's a background enhancement
        return {
            success: false,
            errorMessage: errorMessage
        };
    }
}

/**
 * Generates an AI-powered analysis of a defendant's case summary.
 * Uses OpenAI to provide editorial overview, key findings, and recommendations.
 *
 * @param {Object} caseSummaryData - The complete case summary data
 * @returns {Promise<Object|null>} AI analysis response or null on error
 */
export async function getCaseSummaryAnalysis(caseSummaryData) {
    try {
        const response = await caseSummaryEndpoints.getAnalysis({
            caseSummary: caseSummaryData
        });
        if (response.status === 200) {
            return response.data;
        }
        return null;
    } catch (error) {
        console.error('Error generating case summary AI analysis:', error);

        const errorMessage = error.response?.data?.errorMessage ||
                           error.response?.data?.message ||
                           'Failed to generate AI analysis.';
        CustomToast({
            title: <div className="text-brand-red">Error</div>,
            children: <div>{errorMessage}</div>,
        });
        return {
            success: false,
            errorMessage: errorMessage
        };
    }
}

/**
 * Gets a comprehensive case summary for a defendant.
 * Includes supervision overview, check-in statistics, location/geofence compliance,
 * risk history, payment summary, court events, and compliance history.
 *
 * @param {number} peopleId - The defendant's ID
 * @returns {Promise<Object|null>} Case summary response or null on error
 */
export async function getCaseSummary(peopleId) {
    try {
        const response = await caseSummaryEndpoints.getByPeopleId(peopleId);
        if (response.status === 200) {
            return response.data;
        }
        return null;
    } catch (error) {
        console.error(`Error fetching case summary for peopleId ${peopleId}:`, error);

        // Handle 404 - defendant not found
        if (error.response?.status === 404) {
            CustomToast({
                title: <div className="text-brand-red">Not Found</div>,
                children: <div>Defendant not found.</div>,
            });
            return null;
        }

        // Handle 403 - unauthorized access
        if (error.response?.status === 403) {
            CustomToast({
                title: <div className="text-brand-red">Access Denied</div>,
                children: <div>You do not have permission to view this case summary.</div>,
            });
            return null;
        }

        const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch case summary.';
        CustomToast({
            title: <div className="text-brand-red">Error</div>,
            children: <div>{errorMessage}</div>,
        });
        return null;
    }
}

/**
 * Formats a case summary for display or export.
 * @param {Object} summary - The case summary response
 * @returns {Object} Formatted summary with display-ready values
 */
export function formatCaseSummaryForDisplay(summary) {
    if (!summary) return null;

    return {
        ...summary,
        // Format dates for display
        supervisionStartDateFormatted: summary.supervisionStartDate
            ? new Date(summary.supervisionStartDate).toLocaleDateString()
            : 'N/A',
        supervisionEndDateFormatted: summary.supervisionEndDate
            ? new Date(summary.supervisionEndDate).toLocaleDateString()
            : 'Ongoing',
        generatedAtFormatted: summary.generatedAt
            ? new Date(summary.generatedAt).toLocaleString()
            : '',
        // Format currency
        bondAmountFormatted: summary.bondAmount
            ? `$${summary.bondAmount.toLocaleString()}`
            : 'N/A',
        // Format percentages
        checkInCompletionRateFormatted: summary.checkIns?.completionRate
            ? `${summary.checkIns.completionRate}%`
            : '0%',
        geofenceComplianceRateFormatted: summary.locationData?.travel?.percentTimeWithinGeofence
            ? `${summary.locationData.travel.percentTimeWithinGeofence}%`
            : '100%',
        paymentCompletionRateFormatted: summary.payments?.paymentCompletionRate
            ? `${summary.payments.paymentCompletionRate}%`
            : '100%',
    };
}

/**
 * Generates a text summary suitable for copy/paste or PDF generation.
 * @param {Object} summary - The case summary response
 * @returns {string} Plain text summary
 */
export function generateTextSummary(summary) {
    if (!summary) return '';

    const lines = [];

    // Header
    lines.push('=' .repeat(60));
    lines.push('CASE SUMMARY REPORT');
    lines.push('=' .repeat(60));
    lines.push('');

    // Defendant Info
    lines.push('DEFENDANT INFORMATION');
    lines.push('-'.repeat(40));
    lines.push(`Name: ${summary.defendantName || 'N/A'}`);
    lines.push(`ID: ${summary.peopleId}`);
    if (summary.aNumber) lines.push(`A-Number: ${summary.aNumber}`);
    if (summary.dateOfBirth) lines.push(`Date of Birth: ${new Date(summary.dateOfBirth).toLocaleDateString()}`);
    lines.push('');

    // Supervision Overview
    lines.push('SUPERVISION OVERVIEW');
    lines.push('-'.repeat(40));
    lines.push(`Start Date: ${summary.supervisionStartDate ? new Date(summary.supervisionStartDate).toLocaleDateString() : 'N/A'}`);
    lines.push(`End Date: ${summary.supervisionEndDate ? new Date(summary.supervisionEndDate).toLocaleDateString() : 'Ongoing'}`);
    lines.push(`Duration: ${summary.supervisionDurationDays} days`);
    lines.push(`Compliance Status: ${summary.complianceStatus || 'N/A'}`);
    if (summary.caseOutcome) lines.push(`Case Outcome: ${summary.caseOutcome}`);
    lines.push('');

    // Bond Info
    if (summary.bondType || summary.bondAmount) {
        lines.push('BOND INFORMATION');
        lines.push('-'.repeat(40));
        if (summary.bondType) lines.push(`Type: ${summary.bondType}`);
        if (summary.bondAmount) lines.push(`Amount: $${summary.bondAmount.toLocaleString()}`);
        if (summary.bondStatus) lines.push(`Status: ${summary.bondStatus}`);
        lines.push('');
    }

    // Check-In Statistics
    lines.push('CHECK-IN STATISTICS');
    lines.push('-'.repeat(40));
    const checkIns = summary.checkIns || {};
    lines.push(`Total Check-Ins: ${checkIns.totalCheckIns || 0}`);
    lines.push(`Completed: ${checkIns.completedCheckIns || 0}`);
    lines.push(`Missed: ${checkIns.missedCheckIns || 0}`);
    lines.push(`Pending: ${checkIns.pendingCheckIns || 0}`);
    lines.push(`Completion Rate: ${checkIns.completionRate || 0}%`);
    if (checkIns.averageResponseTimeMinutes) {
        lines.push(`Avg Response Time: ${checkIns.averageResponseTimeMinutes.toFixed(1)} minutes`);
    }
    lines.push('');

    // Location Compliance
    const location = summary.locationData || {};
    lines.push('LOCATION & GEOFENCE COMPLIANCE');
    lines.push('-'.repeat(40));
    lines.push(`Geofence Violations: ${location.geofenceViolationCount || 0}`);
    lines.push(`Curfew Violations: ${location.curfewViolationCount || 0}`);
    lines.push(`GPS Anomalies: ${location.gpsAnomalyCount || 0}`);
    if (location.travel) {
        lines.push(`Total Location Pings: ${location.travel.totalLocationPings || 0}`);
        lines.push(`Time Within Geofence: ${location.travel.percentTimeWithinGeofence || 100}%`);
        lines.push(`Max Distance from Residence: ${location.travel.maxDistanceFromResidenceMiles?.toFixed(1) || 0} miles`);
        if (location.travel.statesVisited?.length > 0) {
            lines.push(`States Visited: ${location.travel.statesVisited.join(', ')}`);
        }
    }
    lines.push('');

    // Risk Assessment
    const risk = summary.risk || {};
    lines.push('RISK ASSESSMENT');
    lines.push('-'.repeat(40));
    lines.push(`Initial Risk Level: ${risk.initialRiskLevel || 'N/A'}`);
    lines.push(`Current Risk Level: ${risk.currentRiskLevel || 'N/A'}`);
    lines.push(`Risk Trend: ${risk.riskTrend || 'N/A'}`);
    lines.push(`Total Risk Alerts: ${risk.riskAlertCount || 0}`);
    lines.push('');

    // Payments
    const payments = summary.payments || {};
    lines.push('PAYMENT SUMMARY');
    lines.push('-'.repeat(40));
    lines.push(`Total Due: $${(payments.totalDue || 0).toLocaleString()}`);
    lines.push(`Total Paid: $${(payments.totalPaid || 0).toLocaleString()}`);
    lines.push(`Outstanding: $${(payments.outstanding || 0).toLocaleString()}`);
    lines.push(`Payment Completion Rate: ${payments.paymentCompletionRate || 100}%`);
    lines.push('');

    // Court Events
    const court = summary.courtEvents || {};
    lines.push('COURT EVENTS');
    lines.push('-'.repeat(40));
    lines.push(`Total Court Events: ${court.totalCourtEvents || 0}`);
    lines.push(`Appeared: ${court.appearedCount || 0}`);
    lines.push(`No-Shows: ${court.noShowCount || 0}`);
    lines.push(`Pending: ${court.pendingCount || 0}`);
    lines.push('');

    // Footer
    lines.push('=' .repeat(60));
    lines.push(`Generated: ${summary.generatedAt ? new Date(summary.generatedAt).toLocaleString() : new Date().toLocaleString()}`);
    lines.push('SafePath Case Summary Report');
    lines.push('=' .repeat(60));

    return lines.join('\n');
}
